import type { CoworkFileCatalogFacet } from "@/cowork/api"
import { useLanguage } from "@/context/language"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { createEffect, createMemo, createUniqueId, For, on, onCleanup, onMount, Show, type JSX } from "solid-js"

const fileCategories = ["all", "document", "spreadsheet", "pdf", "dataset"] as const
export type FileCategory = (typeof fileCategories)[number]

const documentTypeLabels: Record<string, string> = {
  invoice: "cowork.accounting.documentType.invoice",
  receipt: "cowork.accounting.documentType.receipt",
  bank_statement: "cowork.accounting.documentType.bank_statement",
  payroll: "cowork.enterprise.files.filters.payroll",
  unknown: "cowork.enterprise.files.filters.unknownType",
}

export function documentTypeLabelKey(type?: string) {
  return documentTypeLabels[type ?? "unknown"] ?? documentTypeLabels.unknown
}

export function documentTypeLabel(type: string | undefined, translate: (key: string) => string) {
  return type && !documentTypeLabels[type] ? type.replaceAll("_", " ") : translate(documentTypeLabelKey(type))
}

export function FileCatalogFilters(props: {
  scopeKey: string
  category: FileCategory
  onCategory: (category: FileCategory) => void
  accounting: boolean
  children?: JSX.Element
  documentTypes: CoworkFileCatalogFacet[]
  selectedTypes: string[]
  onTypes: (types: string[]) => void
  onClear: () => void
  canClear: boolean
  shown: number
  total: number
}) {
  const language = useLanguage()
  const categoryGroup = createUniqueId()
  let categoryMenu: HTMLDetailsElement | undefined
  let typeMenu: HTMLDetailsElement | undefined
  const closeMenus = () => {
    if (categoryMenu) categoryMenu.open = false
    if (typeMenu) typeMenu.open = false
  }
  onMount(() => {
    const dismissOutside = (event: Event) => {
      const target = event.target
      if (!(target instanceof Node)) return
      for (const menu of [categoryMenu, typeMenu]) {
        if (menu?.open && !menu.contains(target)) menu.open = false
      }
    }
    const dismissEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      const menu = [categoryMenu, typeMenu].find((item) => item?.open)
      if (!menu) return
      event.preventDefault()
      event.stopPropagation()
      closeMenus()
      menu.querySelector("summary")?.focus()
    }
    document.addEventListener("click", dismissOutside, true)
    document.addEventListener("focusin", dismissOutside, true)
    document.addEventListener("keydown", dismissEscape, true)
    onCleanup(() => {
      document.removeEventListener("click", dismissOutside, true)
      document.removeEventListener("focusin", dismissOutside, true)
      document.removeEventListener("keydown", dismissEscape, true)
    })
  })
  createEffect(
    on(
      () => props.scopeKey,
      (key, previous) => {
        if (key === previous) return
        closeMenus()
      },
    ),
  )
  const typeLabel = (type: string) => documentTypeLabel(type, language.t)
  const toggle = (values: string[], id: string, checked: boolean) =>
    checked ? [...new Set([...values, id])] : values.filter((value) => value !== id)
  const chips = createMemo(() => [
    ...props.selectedTypes.map((id) => ({
      id: `type:${id}`,
      label: typeLabel(id),
      remove: () => props.onTypes(props.selectedTypes.filter((value) => value !== id)),
    })),
  ])
  return (
    <div class="mb-4 space-y-3 text-[12px]">
      <div class="flex flex-wrap items-start gap-2">
        <details ref={(element) => (categoryMenu = element)} class="relative">
          <summary class="cursor-pointer rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2">
            {language.t("cowork.enterprise.files.filters.fileType")}
            <Show when={props.category !== "all"}>
              <span class="ml-2">({language.t(`cowork.enterprise.files.filter.${props.category}`)})</span>
            </Show>
          </summary>
          <div class="absolute left-0 top-full z-30 mt-1 w-64 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-2 shadow-[var(--v2-elevation-raised)]">
            <For each={fileCategories}>
              {(category) => (
                <label class="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-v2-overlay-simple-overlay-hover">
                  <input
                    type="radio"
                    name={categoryGroup}
                    value={category}
                    checked={props.category === category}
                    onChange={() => props.onCategory(category)}
                  />
                  <span class="flex-1">{language.t(`cowork.enterprise.files.filter.${category}`)}</span>
                </label>
              )}
            </For>
          </div>
        </details>
        <Show when={props.accounting || props.documentTypes.some((type) => type.id !== "unknown" && type.count > 0)}>
          <details ref={(element) => (typeMenu = element)} class="relative">
            <summary class="cursor-pointer rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2">
              {language.t("cowork.accounting.extractions.fact.documentType")}
              <Show when={props.selectedTypes.length}>
                <span class="ml-2">({props.selectedTypes.length})</span>
              </Show>
            </summary>
            <div class="absolute left-0 top-full z-30 mt-1 w-64 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-2 shadow-[var(--v2-elevation-raised)]">
              <For each={props.documentTypes}>
                {(type) => (
                  <label class="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-v2-overlay-simple-overlay-hover">
                    <input
                      type="checkbox"
                      checked={props.selectedTypes.includes(type.id)}
                      onChange={(event) =>
                        props.onTypes(toggle(props.selectedTypes, type.id, event.currentTarget.checked))
                      }
                    />
                    <span class="flex-1">{typeLabel(type.id)}</span>
                    <span class="text-v2-text-text-muted">{type.count}</span>
                  </label>
                )}
              </For>
            </div>
          </details>
        </Show>
        <div class="w-full min-w-0 lg:w-[320px]">{props.children}</div>
        <span class="ml-auto py-2 text-v2-text-text-muted">
          {language.t("cowork.enterprise.files.filters.results", { shown: props.shown, total: props.total })}
        </span>
      </div>
      <Show when={props.canClear}>
        <div class="flex flex-wrap items-center gap-2">
          <For each={chips()}>
            {(chip) => (
              <button
                type="button"
                class="flex items-center gap-2 rounded-md bg-v2-background-bg-layer-03 px-2 py-1"
                aria-label={language.t("cowork.enterprise.files.filters.remove", { label: chip.label })}
                onClick={chip.remove}
              >
                {chip.label}
                <IconV2 name="close" size="small" />
              </button>
            )}
          </For>
          <button type="button" class="px-2 py-1 underline" onClick={props.onClear}>
            {language.t("cowork.enterprise.files.filters.clear")}
          </button>
        </div>
      </Show>
    </div>
  )
}
